import { Link } from "react-router";
import { useTranslations } from "use-intl";
import { useState } from "react";
import { ChevronRight, PlayCircle, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MUSCLES } from "@/lib/exercise-meta";
import { cn } from "@/lib/utils";

type Entry = {
  id: string;
  name: string;
  type: string;
  muscles: string[];
  equipment: string;
  custom: boolean;
  hasVideo: boolean;
};

export function ExerciseList({ items }: { items: Entry[] }) {
  const t = useTranslations("exercises");
  const tm = useTranslations("muscles");
  const te = useTranslations("equipment");
  const [q, setQ] = useState("");
  const [muscle, setMuscle] = useState<string | null>(null);
  const muscleLabel = (m: string) => (tm.has(m as "abs") ? tm(m as "abs") : m);

  const visible = items.filter(
    (e) =>
      (!q || e.name.toLowerCase().includes(q.toLowerCase())) && (!muscle || e.muscles.includes(muscle)),
  );
  const used = MUSCLES.filter((m) => items.some((e) => e.muscles.includes(m)));

  return (
    <div className="mt-4">
      <div className="relative">
        <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="h-11 pl-9 text-base" />
      </div>
      <div className="-mx-4 mt-3 flex gap-1.5 no-scrollbar overflow-x-auto px-4 pb-1">
        {used.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMuscle(muscle === m ? null : m)}
            className={cn(
              "shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
              muscle === m ? "border-primary bg-primary text-primary-foreground" : "bg-background",
            )}
          >
            {muscleLabel(m)}
          </button>
        ))}
      </div>
      <ul className="mt-3 divide-y rounded-2xl border bg-card">
        {visible.map((e) => (
          <li key={e.id}>
            <Link to={`/exercises/${e.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 font-medium">
                  <span className="truncate">{e.name}</span>
                  {e.hasVideo && <PlayCircle className="size-4 shrink-0 text-primary" />}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {te.has(e.equipment as "machine") ? te(e.equipment as "machine") : e.equipment} ·{" "}
                  {e.muscles.map(muscleLabel).join(", ")}
                </p>
              </div>
              {e.custom && <Badge variant="secondary">{t("custom")}</Badge>}
              <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            </Link>
          </li>
        ))}
        {visible.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted-foreground">{t("empty")}</li>}
      </ul>
    </div>
  );
}
